'use client';

import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import { cva } from 'class-variance-authority';
import cn from 'classnames';
import Link from '../Link';
import { forwardRef } from 'react';
import { ButtonProps, ButtonPropsWithVariants } from './Button.types';
import { TertiaryButton } from './TertiaryButton';

export const ButtonBaseClasses = 'transition-colors duration-400 ease-appear rounded-[64px] text-white font-sans font-weight-medium font-size-p2 font-line-height-p2 font-letter-spacing-p2 focus-visible:button-focus';

const buttonVariants = cva(
  ButtonBaseClasses,
  {
    variants: {
      variant: {
        primary: 'py-xs px-s',
        secondary: 'py-2xs px-xs',
        tertiary: 'py-xs px-s flex items-center justify-center button-text-inverted space-x-xs'
      },
      buttonType: {
        filled: '',
        outlined: 'bg-transparent ',
      },
      disabled: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Filled
      {
        variant: ['primary', 'secondary'],
        buttonType: 'filled',
        class: 'button-surface button-text-default hover:button-surface-active',
      },
      // Outlined
      {
        variant: ['primary', 'secondary'],
        buttonType: 'outlined',
        class: 'button-border button-text-inverted hover:button-border-hover hover:button-surface-outline-hover active:button-surface-outline-active active:button-text-outline-active',
      },
      // Disabled
      {
        variant: ['primary', 'secondary'],
        buttonType: 'filled',
        disabled: true,
        class: 'cursor-not-allowed button-surface-disabled button-text-disabled hover:button-surface-disabled hover:button-text-disabled active:button-surface-disabled active:button-text-disabled',
      },
      {
        variant: ['primary', 'secondary'],
        buttonType: 'outlined',
        disabled: true,
        class: 'cursor-not-allowed button-border-disabled button-text-disabled hover:button-border-disabled hover:button-surface-outline-disabled active:button-border-disabled active:button-surface-outline-disabled active:button-text-disabled',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      buttonType: 'filled',
      disabled: false,
    },
  }
);

export const ButtonElement = forwardRef<HTMLButtonElement, ButtonPropsWithVariants>(
  ({ className, variant, buttonType, disabled, text, ...props }, ref) => {
    return (
      variant === 'tertiary' ? (
        // For tertiary variant, ignore buttonType
        <TertiaryButton
          className={cn(buttonVariants({ variant }), className)}
          ref={ref}
          {...props}
        >
          {text}
        </TertiaryButton>
      ) : (
        <button
          className={cn(buttonVariants({ variant, buttonType, disabled }), className)}
          ref={ref}
          {...props}
        >
          {text}
        </button>
      )
    );
  }
);

ButtonElement.displayName = 'ButtonElement';


const Button = ({ action, href, target, text, disabled, ...props }: ButtonProps) => {
  const { modalActionHandler } = useModalButtonActions();

  if (action === 'url' && href) {
    return (
      <Link href={href} target={target || '_self'}>
        <ButtonElement {...props} text={text} disabled={disabled} />
      </Link>
    );
  }
  if (action === 'trial') {
    return (
      <ButtonElement {...props} onClick={() => modalActionHandler('trial')} text={text} disabled={disabled} />
    );
  }
  if (action === 'demo') {
    return (
      <ButtonElement {...props} onClick={() => modalActionHandler('demo')} text={text} disabled={disabled} />
    );
  }
  return <ButtonElement {...props} text={text} disabled={disabled} />;
};

export { Button, buttonVariants };
